/**
 * renderDocTemplate.js — placeholder filling for legal document templates.
 *
 * Deliberately NOT a general template engine. Legal templates use visible bracketed blanks —
 * [COMPANY NAME] — and an unfilled blank must stay visible so the founder sees what is still
 * missing. Nothing is ever silently dropped and nothing renders as "undefined".
 *
 * Rules
 *   - A token is UPPERCASE letters, digits, spaces, slashes and hyphens inside square brackets.
 *   - A token with a mapped, non-empty value is replaced with that value.
 *   - A token with no value is left exactly as written, so it still reads as a blank to fill.
 *   - Filled values are wrapped by `markFilled` when the caller wants them highlighted in the UI.
 *
 * No dependencies.
 */

export const TOKEN_RE = /\[([A-Z0-9][A-Z0-9 ./-]*)\]/g;

export const DISCLAIMER = [
  "IMPORTANT: This template is provided for educational and informational purposes only and does",
  "not constitute legal advice. Laws and contractual requirements vary by jurisdiction, transaction,",
  "company structure, and circumstances. Founders should have important agreements reviewed by a",
  "qualified attorney before signing or relying upon them.",
].join(" ");

const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

/** Every distinct token in a template body, in first-appearance order. */
export function extractTokens(body) {
  const seen = new Set();
  const out = [];
  for (const m of String(body || "").matchAll(TOKEN_RE)) {
    const token = m[0];
    if (!seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

/**
 * Build the token -> value map for a template from a brand/project profile.
 * `template.placeholders` declares which profile key (if any) feeds each token.
 * Explicit `overrides` (what the founder typed into the form) always win.
 */
export function buildTokenValues(template, profile = {}, overrides = {}) {
  const values = {};
  for (const p of template?.placeholders || []) {
    const fromProfile = p.brandKey ? profile?.[p.brandKey] : undefined;
    const fromOverride = overrides?.[p.token];
    const v = isFilled(fromOverride) ? fromOverride : fromProfile;
    if (isFilled(v)) values[p.token] = String(v).trim();
  }
  // Allow overrides for tokens the template did not declare.
  for (const [token, v] of Object.entries(overrides || {})) {
    if (isFilled(v)) values[token] = String(v).trim();
  }
  return values;
}

/**
 * Fill a template body.
 *
 * @param {string} body
 * @param {Record<string,string>} values  token -> replacement, e.g. { "[COMPANY NAME]": "Acme" }
 * @param {{ markFilled?: (value:string)=>string }} [opts]
 * @returns {string}
 */
export function fillTemplate(body, values = {}, opts = {}) {
  const mark = typeof opts.markFilled === "function" ? opts.markFilled : (v) => v;
  return String(body || "").replace(TOKEN_RE, (token) => {
    const v = values[token];
    return isFilled(v) ? mark(String(v)) : token;
  });
}

/** How complete is this template given the values we have? */
export function templateCompleteness(body, values = {}) {
  const tokens = extractTokens(body);
  if (tokens.length === 0) return { total: 0, filled: 0, missing: [], percent: 100 };
  const missing = tokens.filter((t) => !isFilled(values[t]));
  const filled = tokens.length - missing.length;
  return {
    total: tokens.length,
    filled,
    missing,
    percent: Math.round((filled / tokens.length) * 100),
  };
}

/**
 * Assemble the full document a founder copies or downloads: disclaimer, then the filled body.
 * The disclaimer is not optional and is not configurable — it goes on every rendered template.
 */
export function renderDocument(template, profile = {}, overrides = {}) {
  const values = buildTokenValues(template, profile, overrides);
  const body = fillTemplate(template?.body || "", values);
  const completeness = templateCompleteness(template?.body || "", values);
  const header = [
    template?.title || "Agreement",
    "",
    DISCLAIMER,
    "",
    "─".repeat(78),
    "",
  ].join("\n");
  return { text: header + body, values, completeness };
}

/** Validate a template object at build time. Returns an array of problem strings. */
export function validateTemplate(template) {
  const problems = [];
  const t = template || {};
  if (!t.id) problems.push("missing id");
  if (!t.docId) problems.push("missing docId");
  if (!t.title) problems.push("missing title");
  if (!t.version) problems.push("missing version");
  if (!t.body || String(t.body).trim().length < 400) problems.push("body missing or too short");
  if (!t.founderGuide) problems.push("missing founderGuide");
  else {
    const g = t.founderGuide;
    if (!g.useThisWhen) problems.push("founderGuide.useThisWhen missing");
    if (!g.whyItMatters) problems.push("founderGuide.whyItMatters missing");
    if (!Array.isArray(g.negotiateCarefully) || g.negotiateCarefully.length < 5) {
      problems.push("founderGuide.negotiateCarefully needs at least 5 items");
    }
    if (!Array.isArray(g.redFlags) || g.redFlags.length < 3) {
      problems.push("founderGuide.redFlags needs at least 3 items");
    }
  }

  const declared = new Set((t.placeholders || []).map((p) => p.token));
  const used = extractTokens(t.body || "");
  for (const token of used) {
    if (!declared.has(token)) problems.push(`token ${token} used in body but not declared in placeholders`);
  }
  for (const p of t.placeholders || []) {
    if (!/^\[[A-Z0-9][A-Z0-9 ./-]*\]$/.test(p.token || "")) problems.push(`bad token format: ${p.token}`);
    if (!p.label) problems.push(`placeholder ${p.token} missing label`);
    if (!used.includes(p.token)) problems.push(`placeholder ${p.token} declared but never used in body`);
  }

  // Guard against the two things that would make a template dangerous.
  if (/\{\{/.test(t.body || "")) problems.push("body contains a {{ }} placeholder — use [BRACKET] tokens");
  if (/\b(guarantees you|is legally binding in all|no attorney needed|replaces legal counsel)\b/i.test(t.body || "")) {
    problems.push("body contains language implying it replaces counsel");
  }
  return problems;
}

export default {
  DISCLAIMER,
  TOKEN_RE,
  extractTokens,
  buildTokenValues,
  fillTemplate,
  templateCompleteness,
  renderDocument,
  validateTemplate,
};
